/* eslint-disable @typescript-eslint/no-unsafe-argument */
import ExploreIcon from "@mui/icons-material/Explore";
import LayersIcon from "@mui/icons-material/Layers";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Drawer,
  Fab,
  Grid,
  IconButton,
  Slide,
  Tab,
  Tabs,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as Cesium from "cesium";
import PinIcon from "mdi-react/PinIcon";
import PinOffIcon from "mdi-react/PinOffIcon";
import React, { useCallback, useEffect, useState } from "react";
import { If } from "react-extras";
import { useTranslation } from "react-i18next";
import {
  readMapSettings,
  updateMapSettings,
} from "~/pages/MapPage/functions/localMapSettings";
import { MapDrawerTabs } from "../../components/MapDrawer";
import { useMapStyles } from "../../MapPage";
import CesiumDrawerDataItems from "./CesiumDrawerDataItems";
import { CesiumLayerSettings } from "./CesiumLayerSettings";

const useStyles = makeStyles((theme) => ({
  drawerGrid: {
    flexBasis: theme.mapDrawer.width,
  },
  drawer: {
    backgroundColor: theme.palette.primary.light,
    width: theme.mapDrawer.width,
    maxWidth: theme.mapDrawer.maxWidth,
    overflowX: "hidden",
  },
  pinButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pinIcon: {
    fill: theme.palette.primary.contrastText,
  },
  topContainer: {
    boxShadow: theme.shadows[2],
  },
  tabs: {
    color: theme.palette.primary.contrastText,
  },
  tab: {
    margin: 0,
  },
  indicator: {
    "& > span": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
}));

export type CesiumDrawerTabs = "layers" | "data";

interface Props {
  pinned: boolean;
  onPin: () => void;
  viewer: Cesium.Viewer;
}

export interface Layer {
  title: string;
  group: string;
  visible: boolean;
  zIndex: number;
}

const CesiumDrawer = ({ pinned, onPin, viewer }: Props): JSX.Element => {
  const classes = useStyles();
  const mapClasses = useMapStyles();
  const { t } = useTranslation("map");
  const [open, setOpen] = useState(readMapSettings("cesiumDrawerOpen"));
  const [tabValue, setTabValue] = useState<CesiumDrawerTabs>(
    readMapSettings("cesiumDrawerTab"),
  );

  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));

  const handleDrawerToggle = useCallback((state: boolean) => {
    setOpen(state);
    updateMapSettings({ cesiumDrawerOpen: state });
  }, []);

  const handleTabChange = (val: CesiumDrawerTabs) => {
    updateMapSettings({ cesiumDrawerTab: val });
    setTabValue(val);
  };

  useEffect(() => {
    if (!isMedium) return;
    handleDrawerToggle(false);
    if (pinned) onPin();
  }, [isMedium, handleDrawerToggle, pinned, onPin]);

  return (
    <>
      {!pinned && (
        <Tooltip title={t("drawer-button").toString()}>
          <Fab
            color="primary"
            onClick={() => handleDrawerToggle(true)}
            className={mapClasses.settingsButton}
          >
            <TuneIcon />
          </Fab>
        </Tooltip>
      )}
      <Grid item className={pinned ? classes.drawerGrid : ""}>
        <Drawer
          anchor="right"
          variant={pinned ? "permanent" : "temporary"}
          open={open}
          onClose={() => handleDrawerToggle(false)}
          classes={{ paper: classes.drawer }}
          PaperProps={{ elevation: pinned ? 15 : undefined }}
        >
          <Grid container className={classes.topContainer}>
            <Grid item xs={10}>
              <Tabs
                value={tabValue}
                className={classes.tabs}
                TabIndicatorProps={{ className: classes.tab }}
                variant="fullWidth"
                indicatorColor="secondary"
                onChange={(_e, val) => handleTabChange(val)}
                textColor="inherit"
              >
                <Tab
                  className={classes.tab}
                  label={t("objects")}
                  icon={<ExploreIcon />}
                  value={"data" as MapDrawerTabs}
                />
                <Tab
                  className={classes.tab}
                  label={t("layers")}
                  icon={<LayersIcon />}
                  value={"layers" as MapDrawerTabs}
                />
              </Tabs>
            </Grid>

            {!isMedium && (
              <Grid item className={classes.pinButton} xs={2}>
                <Tooltip
                  title={pinned ? t("unpin").toString() : t("pin").toString()}
                >
                  <IconButton onClick={() => onPin()} size="large">
                    {pinned ? (
                      <PinOffIcon className={classes.pinIcon} />
                    ) : (
                      <PinIcon className={classes.pinIcon} />
                    )}
                  </IconButton>
                </Tooltip>
              </Grid>
            )}
          </Grid>
          <If condition={tabValue === "layers"}>
            <Slide in={tabValue === "layers"} direction="left">
              <CesiumLayerSettings viewer={viewer} />
            </Slide>
          </If>
          <If condition={tabValue === "data"}>
            <Slide in={tabValue === "data"} direction="right">
              <CesiumDrawerDataItems viewer={viewer} />
            </Slide>
          </If>
        </Drawer>
      </Grid>
    </>
  );
};

export default CesiumDrawer;
